import { useMutation, useQuery } from "@apollo/client";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import Restore from "@mui/icons-material/Restore";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import {
  ResetReportMutation,
  ResetReportMutationVariables,
  TierIiReportEncampStatus,
  TierIiReportOrgStatus,
  TierIiReportStep,
} from "generated-graphql/graphql";
import { useResetSafetyOverrideAuth } from "hooks/useResetReportAuth";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useReportSteps } from "routes/Customer/Chemicals/Report/useReport";
import { encampStatusToLabel, orgStatusToLabel } from "util/constants";

import { ResetReportExplainer } from "../../../../components/ResetReportExplainer";
import { useReportDetails } from "./useReportDetails";

const RESET_REPORT = gql(`
  mutation ResetReport($inputs: ResetReportInput!) {
    resetReport(inputs: $inputs)
  }
`);

export const REPORT_SAFE_TO_RESET = gql(`
query ReportSafeToReset($reportId: ID!) {
    reportSafeToReset(reportId: $reportId) {
      isSafeToReset
      unsafeResetReasons
      activities {
        isSafeToReset
        activity {
          id
          type
        }
      }
    }
}`);

type Props = { open: boolean; toggleResetReportModal: () => void };

export const ResetReportModal = (props: Props) => {
  const { toggleResetReportModal } = props;
  const { data, refetch } = useReportDetails();
  const alerts = useAlerts();
  const { canOverrideSafetyCheck, loading: authLoading } = useResetSafetyOverrideAuth();

  const [overrideSafetyCheck, setOverrideSafetyCheck] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [orgStatus, setOrgStatus] = useState<TierIiReportOrgStatus>();
  const [encampStatus, setEncampStatus] = useState<TierIiReportEncampStatus>();
  const [shouldForceCancelWorkflow, setShouldForceCancelWorkflow] = useState<boolean>(true);
  const [shouldForceReSnapshot, setShouldForceReSnapshot] = useState<boolean>(false);
  const isFilingCompleteTarget = encampStatus === TierIiReportEncampStatus.FilingComplete;

  const reportSteps = useReportSteps();
  const steps = useMemo(
    () =>
      Object.entries(reportSteps).map(([step, { metaData }]) => ({
        step: step as TierIiReportStep,
        name: metaData.title,
        value: false,
      })),
    [reportSteps]
  );

  const [reportStepsUntouched, setReportStepsUntouched] =
    useState<{ step: TierIiReportStep; value: boolean; name: string }[]>(steps);

  const { reportId } = useParams<{ reportId: string }>();
  const safeToResetQuery = useQuery(REPORT_SAFE_TO_RESET, {
    variables: {
      reportId: reportId ?? "",
    },
    skip: !reportId,
    fetchPolicy: "network-only",
  });

  const safeToReset = safeToResetQuery.data?.reportSafeToReset.isSafeToReset;

  // A reason is always required for any reset
  const hasValidReason = resetReason.trim().length > 0;

  // Form should be disabled if the report is unsafe and user cannot override
  const isFormDisabled = !safeToReset && !canOverrideSafetyCheck;

  // User can reset if:
  // 1. They provided a reason, AND
  // 2. It's safe to reset OR they've overridden the safety check with permission
  const canReset =
    hasValidReason && (safeToReset || (overrideSafetyCheck && canOverrideSafetyCheck));

  useEffect(() => {
    setReportStepsUntouched(steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(steps)]);

  useEffect(() => {
    if (data) {
      setOrgStatus(data.tierIIReport.organizationStatus);
      setEncampStatus(data.tierIIReport.encampStatus);
    }
  }, [data]);

  useEffect(() => {
    if (!isFilingCompleteTarget) {
      return;
    }

    setShouldForceCancelWorkflow(false);
    setShouldForceReSnapshot(false);
  }, [isFilingCompleteTarget]);

  const onClose = useCallback(() => {
    toggleResetReportModal();
    setOverrideSafetyCheck(false);
    setResetReason("");
  }, [toggleResetReportModal]);

  const [resetReportMutation, { loading: isResettingReports }] = useMutation<
    ResetReportMutation,
    ResetReportMutationVariables
  >(RESET_REPORT, {
    onCompleted() {
      refetch();
      alerts.success(`Successfully reset report status.`);
    },
    onError(error) {
      alerts.error("We encountered an error resetting the report.", error);
    },
  });
  const resetReport = useCallback(async () => {
    await resetReportMutation({
      variables: {
        inputs: {
          encampStatus: encampStatus ?? TierIiReportEncampStatus.AwaitingVerification,
          organizationStatus: orgStatus ?? TierIiReportOrgStatus.NotStarted,
          reportId: reportId ?? "",
          safetyOverride: overrideSafetyCheck,
          safetyOverrideReason: overrideSafetyCheck ? resetReason.trim() : undefined,
          resetReason: resetReason.trim(),
          untouchedSteps: reportStepsUntouched
            .filter((reportStep) => reportStep.value)
            .map((reportStep) => reportStep.step),
          shouldForceReSnapshot,
          shouldForceCancelWorkflow,
        },
      },
    });

    onClose();
  }, [
    resetReportMutation,
    encampStatus,
    orgStatus,
    reportId,
    overrideSafetyCheck,
    resetReason,
    reportStepsUntouched,
    shouldForceReSnapshot,
    shouldForceCancelWorkflow,
    onClose,
  ]);
  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      title="Reset Report"
      primaryActionButtonProps={{
        saveText: "Reset report",
        onClick: resetReport,
        loading: isResettingReports || authLoading,
        startIcon: <Restore />,
        disabled: !canReset || !reportId || !encampStatus || !orgStatus,
      }}
    >
      <ResetReportExplainer />
      <Box sx={{ m: "2rem 0" }}>
        {safeToReset ? (
          <SafeResetIndicator />
        ) : (
          <UnsafeResetIndicatorWithOverride
            reportId={reportId}
            unsafeResetReasons={safeToResetQuery.data?.reportSafeToReset.unsafeResetReasons ?? []}
            overrideSafetyCheck={overrideSafetyCheck}
            onChangeOverride={() => setOverrideSafetyCheck(!overrideSafetyCheck)}
            canOverride={canOverrideSafetyCheck}
          />
        )}
      </Box>

      <TextField
        fullWidth
        required
        multiline
        rows={2}
        label="Reason for reset"
        placeholder="Why are you resetting this report?"
        value={resetReason}
        onChange={(e) => setResetReason(e.target.value)}
        sx={{ mb: "1rem" }}
        helperText="Please include relevant context such as Slack threads, Linear tickets, or customer requests."
        disabled={isFormDisabled}
      />
      <Stack direction="row" gap={3}>
        <FormControl fullWidth disabled={isFormDisabled} sx={{ mb: "1rem" }}>
          <InputLabel id="org-status-label">Org Status</InputLabel>
          <Select
            labelId="org-status-label"
            id="org-status"
            label="Org Status"
            value={orgStatus}
            onChange={(e) => setOrgStatus(e.target.value as TierIiReportOrgStatus)}
          >
            {orgStatuses.map((orgStatus) => (
              <MenuItem key={orgStatus} value={orgStatus}>
                {orgStatusToLabel(orgStatus)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={isFormDisabled} sx={{ mb: "1rem" }}>
          <InputLabel id="encamp-status-label">Encamp Status</InputLabel>
          <Select
            labelId="encamp-status-label"
            id="encamp-status"
            label="Encamp Status"
            value={encampStatus}
            onChange={(e) => setEncampStatus(e.target.value as TierIiReportEncampStatus)}
          >
            {encampStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {encampStatusToLabel(status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack direction="row" marginY={2}>
        <FormControlLabel
          label={"Force Cancel Workflow"}
          disabled={isFormDisabled || isFilingCompleteTarget}
          control={
            <Checkbox
              checked={shouldForceCancelWorkflow}
              disabled={isFormDisabled || isFilingCompleteTarget}
              onChange={() => {
                setShouldForceCancelWorkflow((prev) => !prev);
              }}
            />
          }
        />
        <FormControlLabel
          label={"Force Replace Snapshot"}
          disabled={isFormDisabled || isFilingCompleteTarget}
          control={
            <Checkbox
              checked={shouldForceReSnapshot}
              disabled={isFormDisabled || isFilingCompleteTarget}
              onChange={() => {
                setShouldForceReSnapshot((prev) => !prev);
              }}
            />
          }
        />
      </Stack>
      {isFilingCompleteTarget ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Reports cannot force cancel workflow or force replace snapshot when setting Encamp Status
          to Filing Complete.
        </Alert>
      ) : null}
      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>Set report steps to "untouched"</Typography>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setReportStepsUntouched((prev) => prev.map((step) => ({ ...step, value: true })));
            }}
            sx={{
              ...(isFormDisabled && {
                color: "text.disabled",
                pointerEvents: "none",
              }),
            }}
          >
            Check All
          </Link>
        </Stack>
        <Grid container>
          {reportStepsUntouched.map(({ step, name, value }, reportStepIndex) => (
            <Grid item sm={4} key={name}>
              <FormControlLabel
                key={step}
                label={name}
                disabled={isFormDisabled}
                control={
                  <Checkbox
                    disabled={isFormDisabled}
                    checked={value}
                    onChange={() =>
                      setReportStepsUntouched(
                        reportStepsUntouched.map((x, index) => {
                          if (index === reportStepIndex) {
                            return { ...x, value: !x.value };
                          }
                          return x;
                        })
                      )
                    }
                  />
                }
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Dialog>
  );
};

const SafeResetIndicator = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        color: theme.palette.success.main,
        display: "flex",
        alignItems: "end",
      }}
    >
      <CheckCircle />
      <Typography sx={{ marginLeft: "4px" }}>It is safe to reset this report</Typography>
    </Box>
  );
};

const UnsafeResetIndicator = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        color: theme.palette.error.main,
        display: "flex",
        alignItems: "end",
      }}
    >
      <WarningAmberRounded />
      <Typography fontSize={14} sx={{ marginLeft: "4px" }}>
        It is not safe to reset this report
      </Typography>
    </Box>
  );
};

const UnsafeResetIndicatorWithOverride = (props: {
  reportId?: string;
  unsafeResetReasons: string[];
  overrideSafetyCheck: boolean;
  onChangeOverride: () => void;
  canOverride: boolean;
}) => {
  const theme = useTheme();
  const alerts = useAlerts();

  const reportActivitiesUrl = `https://ehs.encamp.com/staff/fulfillment/r/${props.reportId}/activities`;
  const handleCopy = () => {
    navigator.clipboard.writeText(reportActivitiesUrl);
    alerts.info("Copied to clipboard");
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: theme.palette.error.main,
        borderRadius: "3px",
        padding: "8px",
      }}
    >
      <UnsafeResetIndicator />

      <List disablePadding>
        {props.unsafeResetReasons.map((reason) => (
          <ListItem key={reason} sx={{ padding: "0 30px" }}>
            <Typography fontSize={14} sx={{ color: theme.palette.error.main }}>
              - {reason}
            </Typography>
          </ListItem>
        ))}
      </List>

      <Typography fontSize={14} fontWeight="500" sx={{ m: "1rem 0" }}>
        Making changes now may affect the report submission, which has already started. Some steps
        cannot be recalled, such as submitting a payment.
      </Typography>

      {props.canOverride ? (
        <>
          <Typography fontSize={14} fontWeight="500" sx={{ m: "1rem 0" }}>
            Submit a ticket to{" "}
            <Link href={"https://encamp.slack.com/archives/C02EUTUBC1H"}>#engineering-support</Link>{" "}
            for manual review by an engineer with these{" "}
            <Link href={reportActivitiesUrl} target="_blank">
              report details
            </Link>
            .{" "}
            <IconButton onClick={handleCopy} sx={{ padding: 0 }}>
              <CopyAllOutlined sx={{ height: "16px", width: "16px" }} />
            </IconButton>
          </Typography>

          <FormControlLabel
            sx={{ marginLeft: "0px" }}
            control={
              <Checkbox
                sx={{ padding: "9px 4px 9px 0px" }}
                checked={props.overrideSafetyCheck}
                onChange={props.onChangeOverride}
              />
            }
            label={
              <Typography fontSize={14} fontWeight={500}>
                I've reviewed this with an engineer and want to override the safety check
              </Typography>
            }
          />
        </>
      ) : (
        <Typography fontSize={14} fontWeight="500" sx={{ m: "1rem 0" }}>
          If you need this report reset, please reach out in{" "}
          <Link href="https://encamp.slack.com/archives/C02KQS2CWQF" target="_blank">
            #tier-2-basecamp
          </Link>{" "}
          on Slack and tag <strong>@tierii-captains</strong> for assistance.
        </Typography>
      )}
    </Box>
  );
};

const orgStatuses: TierIiReportOrgStatus[] = [
  TierIiReportOrgStatus.NotStarted,
  TierIiReportOrgStatus.NotReporting,
  TierIiReportOrgStatus.Assigned,
  TierIiReportOrgStatus.InProgress,
  TierIiReportOrgStatus.InReview,
  TierIiReportOrgStatus.Verified,
];

const encampStatuses: TierIiReportEncampStatus[] = [
  TierIiReportEncampStatus.AwaitingVerification,
  TierIiReportEncampStatus.Processing,
  TierIiReportEncampStatus.FilingComplete,
  TierIiReportEncampStatus.NotReporting,
];
